import { useEffect, useMemo } from "react";
import {
  isSettingsEvent,
  type SettingsEvent,
} from "@/features/settings/mediators/settingsEvents";
import { createSpeechSettingsMediator } from "@/features/settings/mediators/speechSettingsMediator";
import { SettingRow, SettingSection } from "@/features/settings/ui";
import { useThreadSpeechSettings } from "@/features/thread/hooks/useThreadSpeechSettings";
import {
  loadBrowserSpeechSettings,
  type SpeechSettings,
  saveBrowserSpeechSettings,
} from "@/features/thread/utils/speech";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { Input, Toggle } from "@/shared/ui/form";

interface OtherSettingsViewProps {
  readonly speech: SpeechSettings;
  readonly onEvent: (event: SettingsEvent) => void;
}

/**
 * 読み上げ設定タブのコンテンツ。
 */
export const OtherSettings: React.FunctionComponent = () => {
  const mediator = useMemo(
    () =>
      createSpeechSettingsMediator(loadBrowserSpeechSettings(), (value) =>
        saveBrowserSpeechSettings(value),
      ),
    [],
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (
      !isSettingsEvent(event) ||
      (event.type !== "settings/speech-boolean-changed" &&
        event.type !== "settings/speech-number-changed" &&
        event.type !== "settings/speech-reset")
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  const speech = useMediatorSnapshot(mediator);
  const storedSpeech = useThreadSpeechSettings();
  useEffect(() => {
    mediator.dispatch({
      type: "settings/speech-synchronized",
      settings: storedSpeech,
    });
  }, [mediator, storedSpeech]);

  return (
    <ComponentScope node={node}>
      <OtherSettingsView
        speech={speech}
        onEvent={(event: SettingsEvent): void => {
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
};

export const OtherSettingsView: React.FunctionComponent<
  OtherSettingsViewProps
> = ({ speech, onEvent }: OtherSettingsViewProps) => {
  return (
    <SettingSection title="読み上げ">
      <SettingRow label="常に読み上げ">
        <Toggle
          checked={speech.alwaysEnabled}
          onChange={(value: boolean): void =>
            onEvent({
              type: "settings/speech-boolean-changed",
              field: "alwaysEnabled",
              value,
            })
          }
          aria-label="常に読み上げ"
        />
      </SettingRow>
      <SettingRow label="読み上げ時に自動スクロール">
        <Toggle
          checked={speech.autoScroll}
          onChange={(value: boolean): void =>
            onEvent({
              type: "settings/speech-boolean-changed",
              field: "autoScroll",
              value,
            })
          }
          aria-label="読み上げ自動スクロール"
        />
      </SettingRow>
      <SettingRow label="棒読みちゃんポート" description="既定50080">
        <Input
          type="number"
          min={1}
          max={65535}
          value={speech.port}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            onEvent({
              type: "settings/speech-number-changed",
              field: "port",
              value: event.target.value,
            })
          }
        />
      </SettingRow>
      <SettingRow
        label="ブラウザの読み上げ速度"
        description="棒読みちゃんの速度は棒読みちゃん本体で調整します。"
      >
        <Input
          type="number"
          min={0.5}
          max={2}
          step={0.1}
          value={speech.rate}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            onEvent({
              type: "settings/speech-number-changed",
              field: "rate",
              value: event.target.value,
            })
          }
        />
      </SettingRow>
      <SettingRow
        label="ブラウザの読み上げ音量"
        description="棒読みちゃんの音量は棒読みちゃん本体で調整します。"
      >
        <Input
          type="number"
          min={0}
          max={1}
          step={0.1}
          value={speech.volume}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            onEvent({
              type: "settings/speech-number-changed",
              field: "volume",
              value: event.target.value,
            })
          }
        />
      </SettingRow>
      <SettingRow>
        <button
          type="button"
          onClick={(): void => onEvent({ type: "settings/speech-reset" })}
        >
          読み上げ設定を初期化
        </button>
      </SettingRow>
    </SettingSection>
  );
};
